import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Channel } from '@contracts/events';
import type { SignalAlert, SignalOutcomeEvent } from '@contracts/signals';
import { notifyAlert, notifyOutcome } from '../lib/notify';
import { LiveSocket } from '../lib/ws';
import { useLive } from '../store/live';
import { useSR } from '../store/sr';

const CHANNELS: Channel[] = ['market', 'system', 'signals', 'sr'];

/** Opens the internal WebSocket channels (market, system, signals, sr) for the lifetime of the app. */
export function useLiveFeeds(enabled = true): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const { handleEvent, setConnection, registerGap } = useLive.getState();
    const sockets = CHANNELS.map((channel) => {
      const sock = new LiveSocket(channel, {
        onEvent: (env) => {
          if (channel === 'sr') {
            useSR.getState().handleEvent(env); // the S/R system has its own store
            return;
          }
          handleEvent(channel, env);
          if (env.type === 'signal.alert') notifyAlert(env.payload as unknown as SignalAlert);
          if (env.type === 'signal.outcome') notifyOutcome(env.payload as unknown as SignalOutcomeEvent);
        },
        onState: (state, attempt) => {
          setConnection(channel, state, attempt);
          if (state === 'open') void queryClient.invalidateQueries();
        },
        onGap: () => {
          registerGap();
          void queryClient.invalidateQueries();
        },
      });
      sock.connect();
      return sock;
    });
    return () => sockets.forEach((s) => s.close());
  }, [enabled, queryClient]);
}
